import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import ConfirmModal from '../components/ConfirmModal';
import { ToastContainer } from '../components/UndoToast';
import './AdminPanel.css';

function AdminPanel() {
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ipInfo, setIpInfo] = useState(null);
  const [ipLoading, setIpLoading] = useState(false);
  const [view, setView] = useState('active'); // 'active', 'closed', 'votes', 'bans', 'allowlist', 'settings', 'search', 'analytics', 'logs'
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [votesPage, setVotesPage] = useState(1);
  const [votesPageSize, setVotesPageSize] = useState(5);
  const [expandedSessions, setExpandedSessions] = useState(new Set());
  const [bans, setBans] = useState([]);
  const [allowlist, setAllowlist] = useState([]);
  const [myIp, setMyIp] = useState(null);
  const [newAllowIp, setNewAllowIp] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState({});
  const [settingsSaving, setSettingsSaving] = useState(false);
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFrom, setSearchFrom] = useState('');
  const [searchTo, setSearchTo] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  // Logs state
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsLoading, setLogsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [sessionsData, statsData, bansData, allowlistData, whoamiData] = await Promise.all([
        api.adminGetSessions(),
        api.adminGetStats(),
        api.adminGetBans(),
        api.adminGetAllowlist(),
        api.adminWhoami()
      ]);
      setSessions(sessionsData);
      setStats(statsData);
      setBans(bansData);
      setAllowlist(allowlistData);
      setMyIp(whoamiData.ip);
      setLoading(false);
      setError('');
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin-login');
        return;
      }
      setError('Failed to load data');
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const data = await api.adminGetSettings();
      setSettings(data);
      setSettingsForm(data);
    } catch (err) {
      setError('Failed to load settings');
    }
  };

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    try {
      const result = await api.adminUpdateSettings(settingsForm);
      setSettings(result.settings);
      setSettingsForm(result.settings);
      addToast('Settings saved', null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
      setTimeout(() => setError(''), 3000);
    }
    setSettingsSaving(false);
  };

  const handleSearch = async () => {
    setSearchLoading(true);
    try {
      const results = await api.adminSearchSessions(searchQuery, searchFrom, searchTo);
      setSearchResults(results);
    } catch (err) {
      setError('Search failed');
      setTimeout(() => setError(''), 3000);
    }
    setSearchLoading(false);
  };

  const loadAnalytics = async (days) => {
    setAnalyticsLoading(true);
    try {
      const data = await api.adminGetAnalytics(days);
      setAnalytics(data);
    } catch (err) {
      setError('Failed to load analytics');
      setTimeout(() => setError(''), 3000);
    }
    setAnalyticsLoading(false);
  };

  const loadLogs = async (page = 1) => {
    setLogsLoading(true);
    try {
      const limit = 25;
      const offset = (page - 1) * limit;
      const data = await api.adminGetLogs(limit, offset);
      setLogs(data.logs);
      setLogsTotal(data.total);
      setLogsPage(page);
    } catch (err) {
      setError('Failed to load logs');
      setTimeout(() => setError(''), 3000);
    }
    setLogsLoading(false);
  };

  const addToast = useCallback((message, undoAction) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, undoAction }]);
    return id;
  }, []);

  const handleToastUndo = useCallback(async (toastId) => {
    const toast = toasts.find(t => t.id === toastId);
    setToasts(prev => prev.filter(t => t.id !== toastId));
    if (toast?.undoAction) {
      await toast.undoAction();
      await loadData();
    }
  }, [toasts]);

  const handleToastExpire = useCallback((toastId) => {
    setToasts(prev => prev.filter(t => t.id !== toastId));
  }, []);

  const handleCloseSession = async (code) => {
    // Optimistic: close immediately, offer undo (reopen)
    try {
      await api.adminCloseSession(code);
      await loadData();
      addToast(`Session ${code} closed`, async () => {
        await api.adminReopenSession(code);
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to close session');
    }
  };

  const handleReopenSession = async (code) => {
    // Optimistic: reopen immediately, offer undo (close)
    try {
      await api.adminReopenSession(code);
      await loadData();
      addToast(`Session ${code} reopened`, async () => {
        await api.adminCloseSession(code);
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reopen session');
    }
  };

  const handleDeleteSession = (code) => {
    setConfirmModal({
      title: 'Delete Session',
      message: `Permanently delete session ${code}? This cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.adminDeleteSession(code);
          await loadData();
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to delete session');
        }
      }
    });
  };

  const handlePurgeClosed = () => {
    const closedCount = sessions.filter(s => s.closed).length;
    if (closedCount === 0) {
      setError('No closed sessions to purge.');
      setTimeout(() => setError(''), 3000);
      return;
    }
    setConfirmModal({
      title: 'Purge Closed Sessions',
      message: `Permanently delete all ${closedCount} closed session${closedCount > 1 ? 's' : ''}? This cannot be undone.`,
      confirmText: `Delete ${closedCount} session${closedCount > 1 ? 's' : ''}`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.adminPurgeClosed();
          await loadData();
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to purge sessions');
        }
      }
    });
  };

  const handleExport = async (format) => {
    try {
      const downloadToken = await api.adminGetExportToken();
      window.open(api.adminExportUrl(format, downloadToken), '_blank');
    } catch (err) {
      setError('Failed to generate export link');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin-login');
  };

  const handleIpLookup = async (ip) => {
    setIpLoading(true);
    setIpInfo(null);
    try {
      const data = await api.adminIpLookup(ip);
      setIpInfo(data);
    } catch (err) {
      setIpInfo({ ip, error: 'Lookup failed' });
    }
    setIpLoading(false);
  };

  const handleBanIp = async (ip) => {
    const reason = window.prompt(`Ban IP ${ip}?\nEnter a reason (optional):`);
    if (reason === null) return;
    try {
      await api.adminBanIp(ip, reason);
      await loadData();
      addToast(`Banned ${ip}`, async () => {
        await api.adminUnbanIp(ip);
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to ban IP');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleUnbanIp = async (ip) => {
    try {
      await api.adminUnbanIp(ip);
      await loadData();
      addToast(`Unbanned ${ip}`, async () => {
        await api.adminBanIp(ip, 'Re-banned via undo');
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unban IP');
      setTimeout(() => setError(''), 3000);
    }
  };

  const isIpBanned = (ip) => bans.some(b => b.ip === ip);

  const handleAllowIp = async (ip, note) => {
    const cleaned = (ip || '').trim();
    if (!cleaned) return;
    try {
      await api.adminAllowIp(cleaned, note || '');
      setNewAllowIp('');
      await loadData();
      addToast(`Allowlisted ${cleaned}`, async () => {
        await api.adminUnallowIp(cleaned);
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to allowlist IP');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleUnallowIp = async (ip) => {
    try {
      await api.adminUnallowIp(ip);
      await loadData();
      addToast(`Removed ${ip} from allowlist`, async () => {
        await api.adminAllowIp(ip, 'Re-added via undo');
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove IP');
      setTimeout(() => setError(''), 3000);
    }
  };

  const isIpAllowlisted = (ip) => allowlist.some(a => a.ip === ip);

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString([], { 
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="panel-container"><p>Loading...</p></div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="panel-container">
        <div className="panel-header">
          <h1>Admin Panel</h1>
          <div className="header-actions">
            <button className="header-btn export-btn" onClick={() => handleExport('csv')} title="Export CSV">📥 CSV</button>
            <button className="header-btn export-btn" onClick={() => handleExport('json')} title="Export JSON">📥 JSON</button>
            <button className="header-btn purge-btn" onClick={handlePurgeClosed} title="Delete all closed sessions">🗑️ Purge Closed</button>
            <button className="logout-btn" onClick={handleLogout}>Logout</button>
          </div>
        </div>

        {error && <p className="panel-error">{error}</p>}

        {/* IP Lookup Popup */}
        {ipInfo && (
          <div className="ip-popup-overlay" onClick={() => setIpInfo(null)}>
            <div className="ip-popup" onClick={e => e.stopPropagation()}>
              <h3>IP Lookup: {ipInfo.ip}</h3>
              {ipInfo.error ? (
                <p className="ip-error">{ipInfo.error}</p>
              ) : (
                <div className="ip-details">
                  <p><strong>Country:</strong> {ipInfo.country}</p>
                  <p><strong>Region:</strong> {ipInfo.region}</p>
                  <p><strong>City:</strong> {ipInfo.city}</p>
                  <p><strong>ISP:</strong> {ipInfo.isp}</p>
                  <p><strong>Org:</strong> {ipInfo.org}</p>
                </div>
              )}
              <button className="table-btn close-btn" onClick={() => setIpInfo(null)}>Close</button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="stats-grid">
            <div
              className={`stat-card clickable ${view === 'active' ? 'stat-active' : ''}`}
              onClick={() => { setView('active'); setCurrentPage(1); }}
            >
              <div className="stat-value">{stats.active_sessions}</div>
              <div className="stat-label">Active Sessions</div>
            </div>
            <div
              className={`stat-card clickable ${view === 'closed' ? 'stat-active' : ''}`}
              onClick={() => { setView('closed'); setCurrentPage(1); }}
            >
              <div className="stat-value">{stats.closed_sessions}</div>
              <div className="stat-label">Closed Sessions</div>
            </div>
            <div
              className={`stat-card clickable ${view === 'votes' ? 'stat-active' : ''}`}
              onClick={() => setView('votes')}
            >
              <div className="stat-value">{stats.total_votes}</div>
              <div className="stat-label">Total Votes</div>
            </div>
            <div
              className={`stat-card stat-ban clickable ${view === 'bans' ? 'stat-active' : ''}`}
              onClick={() => setView('bans')}
            >
              <div className="stat-value">{stats.banned_ips}</div>
              <div className="stat-label">Banned IPs</div>
            </div>
            <div
              className={`stat-card stat-allow clickable ${view === 'allowlist' ? 'stat-active' : ''}`}
              onClick={() => setView('allowlist')}
            >
              <div className="stat-value">{stats.allowed_ips}</div>
              <div className="stat-label">Allowed IPs</div>
            </div>
            <div className="stat-card stat-uptime clickable" onClick={() => { setView('settings'); loadSettings(); }}>
              <div className="stat-value">{stats.uptime}</div>
              <div className="stat-label">⚙ Settings</div>
            </div>
            <div
              className={`stat-card clickable ${view === 'search' ? 'stat-active' : ''}`}
              onClick={() => { setView('search'); setSearchResults(null); }}
            >
              <div className="stat-value">🔍</div>
              <div className="stat-label">Search</div>
            </div>
            <div
              className={`stat-card clickable ${view === 'analytics' ? 'stat-active' : ''}`}
              onClick={() => { setView('analytics'); loadAnalytics(analyticsDays); }}
            >
              <div className="stat-value">📊</div>
              <div className="stat-label">Analytics</div>
            </div>
            <div
              className={`stat-card clickable ${view === 'logs' ? 'stat-active' : ''}`}
              onClick={() => { setView('logs'); loadLogs(1); }}
            >
              <div className="stat-value">📋</div>
              <div className="stat-label">Audit Logs</div>
            </div>
          </div>
        )}

        {/* Votes Breakdown (shown when Total Votes card clicked) */}
        {view === 'votes' && (() => {
          // Sort sessions by newest vote timestamp, then paginate
          const withVotes = sessions
            .filter(s => s.votes && s.votes.length > 0)
            .sort((a, b) => {
              const latestA = a.votes.reduce((max, v) => v.timestamp > max ? v.timestamp : max, '');
              const latestB = b.votes.reduce((max, v) => v.timestamp > max ? v.timestamp : max, '');
              return latestB.localeCompare(latestA);
            });
          const totalVotesPages = Math.max(1, Math.ceil(withVotes.length / votesPageSize));
          const vPage = Math.min(votesPage, totalVotesPages);
          const paginatedVotes = withVotes.slice((vPage - 1) * votesPageSize, vPage * votesPageSize);

          // Auto-expand first session on current page
          const firstCode = paginatedVotes.length > 0 ? paginatedVotes[0].code : null;

          const toggleExpanded = (code) => {
            setExpandedSessions(prev => {
              const next = new Set(prev);
              if (next.has(code)) next.delete(code);
              else next.add(code);
              return next;
            });
          };

          const isExpanded = (code) => expandedSessions.has(code) || (vPage === 1 && code === firstCode && expandedSessions.size === 0);

          return (
            <div className="panel-section votes-breakdown">
              <h2>Votes by Session</h2>
              {withVotes.length === 0 ? (
                <p className="no-data">No votes yet</p>
              ) : (
                <>
                  {paginatedVotes.map(session => (
                    <div key={session.code} className="vote-group">
                      <h3
                        className="vote-group-header clickable"
                        onClick={() => toggleExpanded(session.code)}
                      >
                        <span className="vote-group-toggle">{isExpanded(session.code) ? '▼' : '▶'}</span>
                        {session.code}
                        <span className={`vote-group-badge ${session.closed ? 'badge-closed' : 'badge-active'}`}>
                          {session.closed ? 'Closed' : 'Active'}
                        </span>
                        <span className="vote-group-count">{session.votes.length} votes</span>
                        <span className="vote-group-avg">Avg: {session.average > 0 ? session.average.toFixed(2) : '—'}</span>
                      </h3>
                      {isExpanded(session.code) && (
                        <table className="votes-table">
                          <thead>
                            <tr>
                              <th>Voter</th>
                              <th>Vote</th>
                              <th>IP</th>
                            </tr>
                          </thead>
                          <tbody>
                            {session.votes.map((v, i) => (
                              <tr key={i}>
                                <td>{v.name}</td>
                                <td>{v.vote}</td>
                                <td className="ip-cell">
                                  <span className="ip-text">{v.ip}</span>
                                  <button
                                    className="ip-lookup-btn"
                                    onClick={() => handleIpLookup(v.ip)}
                                    disabled={ipLoading}
                                    title="Lookup IP location"
                                  >
                                    🌍
                                  </button>
                                  <button
                                    className={`ip-lookup-btn ${isIpBanned(v.ip) ? 'banned' : ''}`}
                                    onClick={() => isIpBanned(v.ip) ? handleUnbanIp(v.ip) : handleBanIp(v.ip)}
                                    title={isIpBanned(v.ip) ? 'Unban IP' : 'Ban IP'}
                                  >
                                    {isIpBanned(v.ip) ? '✅' : '🚫'}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  ))}
                  {totalVotesPages > 1 && (
                    <div className="pagination">
                      <button
                        className="page-btn"
                        disabled={vPage <= 1}
                        onClick={() => setVotesPage(vPage - 1)}
                      >
                        ← Previous
                      </button>
                      <span className="page-info">Page {vPage} of {totalVotesPages}</span>
                      <button
                        className="page-btn"
                        disabled={vPage >= totalVotesPages}
                        onClick={() => setVotesPage(vPage + 1)}
                      >
                        Next →
                      </button>
                      <select
                        className="page-size-select"
                        value={votesPageSize}
                        onChange={e => { setVotesPageSize(Number(e.target.value)); setVotesPage(1); }}
                      >
                        <option value={5}>5 per page</option>
                        <option value={10}>10 per page</option>
                        <option value={25}>25 per page</option>
                      </select>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* Banned IPs List (shown when Banned IPs card clicked) */}
        {view === 'bans' && (
          <div className="panel-section">
            <h2>Banned IPs ({bans.length})</h2>
            {bans.length === 0 ? (
              <p className="no-data">No banned IPs</p>
            ) : (
              <table className="sessions-table">
                <thead>
                  <tr>
                    <th>IP Address</th>
                    <th>Banned At</th>
                    <th>Reason</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bans.map(ban => (
                    <tr key={ban.ip}>
                      <td className="ip-cell">
                        <span className="ip-text">{ban.ip}</span>
                        <button
                          className="ip-lookup-btn"
                          onClick={() => handleIpLookup(ban.ip)}
                          disabled={ipLoading}
                          title="Lookup IP location"
                        >
                          🌍
                        </button>
                      </td>
                      <td>{formatTime(ban.banned_at)}</td>
                      <td>{ban.reason}</td>
                      <td>
                        <button
                          className="table-btn unban-btn"
                          onClick={() => handleUnbanIp(ban.ip)}
                        >
                          Unban
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {view === 'allowlist' && (
          <div className="panel-section">
            <h2>Allowed IPs ({allowlist.length})</h2>
            <p className="allowlist-hint">
              IPs listed here bypass rate limiting. Use this for a shared office network
              where many people legitimately vote from one address.
            </p>

            <div className="allowlist-add">
              {myIp && (
                <div className="my-ip-row">
                  <span>Your current IP: <strong>{myIp}</strong></span>
                  <button
                    className="table-btn allow-btn"
                    onClick={() => handleAllowIp(myIp, 'Added from admin panel')}
                    disabled={isIpAllowlisted(myIp)}
                  >
                    {isIpAllowlisted(myIp) ? 'Already allowed' : 'Add my IP'}
                  </button>
                </div>
              )}
              <div className="add-ip-row">
                <input
                  type="text"
                  className="allow-ip-input"
                  placeholder="Add another IP, e.g. 81.20.150.34"
                  value={newAllowIp}
                  onChange={(e) => setNewAllowIp(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAllowIp(newAllowIp, 'Added from admin panel'); }}
                />
                <button
                  className="table-btn allow-btn"
                  onClick={() => handleAllowIp(newAllowIp, 'Added from admin panel')}
                  disabled={!newAllowIp.trim()}
                >
                  Add
                </button>
              </div>
            </div>

            {allowlist.length === 0 ? (
              <p className="no-data">No allowlisted IPs</p>
            ) : (
              <table className="sessions-table">
                <thead>
                  <tr>
                    <th>IP Address</th>
                    <th>Added At</th>
                    <th>Note</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allowlist.map(entry => (
                    <tr key={entry.ip}>
                      <td className="ip-cell">
                        <span className="ip-text">{entry.ip}</span>
                        <button
                          className="ip-lookup-btn"
                          onClick={() => handleIpLookup(entry.ip)}
                          disabled={ipLoading}
                          title="Lookup IP location"
                        >
                          🌍
                        </button>
                      </td>
                      <td>{formatTime(entry.allowed_at)}</td>
                      <td>{entry.note}</td>
                      <td>
                        <button
                          className="table-btn unban-btn"
                          onClick={() => handleUnallowIp(entry.ip)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Settings Panel */}
        {view === 'settings' && settings && (
          <div className="panel-section settings-section">
            <h2>⚙ Server Settings</h2>
            <p className="settings-note">Changes take effect immediately. Reset on server restart.</p>

            <div className="settings-grid">
              <div className="setting-group">
                <label>Default Max Voters (per session)</label>
                <input
                  type="number"
                  min="5" max="100"
                  value={settingsForm.max_voters || ''}
                  onChange={e => setSettingsForm({...settingsForm, max_voters: parseInt(e.target.value) || 0})}
                />
              </div>

              <div className="setting-group">
                <label>Max Concurrent Sessions</label>
                <input
                  type="number"
                  min="1" max="500"
                  value={settingsForm.max_concurrent_sessions || ''}
                  onChange={e => setSettingsForm({...settingsForm, max_concurrent_sessions: parseInt(e.target.value) || 0})}
                />
              </div>

              <div className="setting-group">
                <label>Auto-close after (hours)</label>
                <input
                  type="number"
                  min="0.5" max="48" step="0.5"
                  value={settingsForm.session_auto_close_hours || ''}
                  onChange={e => setSettingsForm({...settingsForm, session_auto_close_hours: parseFloat(e.target.value) || 0})}
                />
              </div>

              <div className="setting-group">
                <label>Rate Limit: Session Create</label>
                <input
                  type="text"
                  placeholder="e.g. 5 per minute"
                  value={settingsForm.rate_limit_session_create || ''}
                  onChange={e => setSettingsForm({...settingsForm, rate_limit_session_create: e.target.value})}
                />
              </div>

              <div className="setting-group">
                <label>Rate Limit: Vote Submit</label>
                <input
                  type="text"
                  placeholder="e.g. 10 per minute"
                  value={settingsForm.rate_limit_vote_submit || ''}
                  onChange={e => setSettingsForm({...settingsForm, rate_limit_vote_submit: e.target.value})}
                />
              </div>

              <div className="setting-group">
                <label>Rate Limit: Admin Login</label>
                <input
                  type="text"
                  placeholder="e.g. 3 per minute"
                  value={settingsForm.rate_limit_admin_login || ''}
                  onChange={e => setSettingsForm({...settingsForm, rate_limit_admin_login: e.target.value})}
                />
              </div>

              <div className="setting-group setting-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={settingsForm.default_anonymous || false}
                    onChange={e => setSettingsForm({...settingsForm, default_anonymous: e.target.checked})}
                  />
                  Default Anonymous Sessions
                </label>
              </div>
            </div>

            <button
              className="save-settings-btn"
              onClick={handleSaveSettings}
              disabled={settingsSaving}
            >
              {settingsSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        )}

        {/* Search Panel */}
        {view === 'search' && (
          <div className="panel-section">
            <h2>🔍 Search Sessions</h2>
            <div className="search-form">
              <div className="search-row">
                <input
                  type="text"
                  placeholder="Search by code or IP..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  className="search-input"
                />
                <button className="search-btn" onClick={handleSearch} disabled={searchLoading}>
                  {searchLoading ? 'Searching...' : 'Search'}
                </button>
              </div>
              <div className="search-row">
                <label>From: <input type="date" value={searchFrom} onChange={e => setSearchFrom(e.target.value)} /></label>
                <label>To: <input type="date" value={searchTo} onChange={e => setSearchTo(e.target.value)} /></label>
              </div>
            </div>
            {searchResults && (
              <div className="search-results">
                <p className="search-count">{searchResults.length} session{searchResults.length !== 1 ? 's' : ''} found</p>
                {searchResults.length > 0 && (
                  <table className="sessions-table">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Created</th>
                        <th>Status</th>
                        <th>Votes</th>
                        <th>Avg</th>
                        <th>Creator IP</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map(s => (
                        <tr key={s.code}>
                          <td className="code-cell">{s.code}</td>
                          <td>{formatTime(s.created_at)}</td>
                          <td><span className={`vote-group-badge ${s.closed ? 'badge-closed' : 'badge-active'}`}>{s.closed ? 'Closed' : 'Active'}</span></td>
                          <td>{s.vote_count}</td>
                          <td>{s.average > 0 ? s.average.toFixed(2) : '—'}</td>
                          <td>{s.creator_ip}</td>
                          <td><button className="table-btn view-btn" onClick={() => navigate(`/admin/${s.code}`)}>View</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}

        {/* Analytics Panel */}
        {view === 'analytics' && (
          <div className="panel-section">
            <h2>📊 Analytics</h2>
            <div className="analytics-period">
              <label>Period: </label>
              <select value={analyticsDays} onChange={e => { const d = Number(e.target.value); setAnalyticsDays(d); loadAnalytics(d); }}>
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={365}>Last year</option>
              </select>
            </div>
            {analyticsLoading ? (
              <p>Loading analytics...</p>
            ) : analytics ? (
              <div className="analytics-grid">
                <div className="analytics-summary">
                  <div className="stat-card"><div className="stat-value">{analytics.total_sessions}</div><div className="stat-label">Total Sessions</div></div>
                  <div className="stat-card"><div className="stat-value">{analytics.total_votes}</div><div className="stat-label">Total Votes</div></div>
                  <div className="stat-card"><div className="stat-value">{analytics.average_confidence}</div><div className="stat-label">Avg Confidence</div></div>
                </div>

                <div className="analytics-section">
                  <h3>Vote Distribution</h3>
                  <div className="distribution-bars">
                    {analytics.vote_distribution.map(d => {
                      const pct = analytics.total_votes > 0 ? Math.round((d.count / analytics.total_votes) * 100) : 0;
                      return (
                        <div key={d.vote_value} className="distribution-row">
                          <span className="dist-label">Vote {d.vote_value}</span>
                          <div className="dist-bar-bg">
                            <div className={`dist-bar dist-bar-${d.vote_value}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="dist-count">{d.count} ({pct}%)</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="analytics-section">
                  <h3>Confidence Trend</h3>
                  {analytics.avg_confidence_per_day.length === 0 ? (
                    <p className="no-data">No data yet</p>
                  ) : (
                    <table className="sessions-table">
                      <thead><tr><th>Date</th><th>Avg Confidence</th><th>Votes</th></tr></thead>
                      <tbody>
                        {analytics.avg_confidence_per_day.map(d => (
                          <tr key={d.day}>
                            <td>{d.day}</td>
                            <td>{d.avg != null ? d.avg.toFixed(2) : '—'}</td>
                            <td>{d.votes}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="analytics-section">
                  <h3>Sessions Per Day</h3>
                  {analytics.sessions_per_day.length === 0 ? (
                    <p className="no-data">No data yet</p>
                  ) : (
                    <table className="sessions-table">
                      <thead><tr><th>Date</th><th>Sessions Created</th></tr></thead>
                      <tbody>
                        {analytics.sessions_per_day.map(d => (
                          <tr key={d.day}><td>{d.day}</td><td>{d.count}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {analytics.top_voters.length > 0 && (
                  <div className="analytics-section">
                    <h3>Top Voters</h3>
                    <table className="sessions-table">
                      <thead><tr><th>Name</th><th>Votes Cast</th><th>Avg Vote</th></tr></thead>
                      <tbody>
                        {analytics.top_voters.map(v => (
                          <tr key={v.voter_name}>
                            <td>{v.voter_name}</td>
                            <td>{v.vote_count}</td>
                            <td>{v.avg_vote != null ? v.avg_vote.toFixed(2) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}

        {/* Audit Logs Panel */}
        {view === 'logs' && (
          <div className="panel-section">
            <h2>📋 Audit Logs</h2>
            {logsLoading ? (
              <p>Loading logs...</p>
            ) : logs.length === 0 ? (
              <p className="no-data">No audit logs yet</p>
            ) : (
              <>
                <table className="sessions-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Action</th>
                      <th>Details</th>
                      <th>Admin IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <tr key={log.id}>
                        <td>{formatTime(log.timestamp)}</td>
                        <td><span className="log-action">{log.action}</span></td>
                        <td>{log.details}</td>
                        <td>{log.admin_ip || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {logsTotal > 25 && (
                  <div className="pagination">
                    <button className="page-btn" disabled={logsPage <= 1} onClick={() => loadLogs(logsPage - 1)}>← Previous</button>
                    <span className="page-info">Page {logsPage} of {Math.ceil(logsTotal / 25)}</span>
                    <button className="page-btn" disabled={logsPage >= Math.ceil(logsTotal / 25)} onClick={() => loadLogs(logsPage + 1)}>Next →</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Sessions Table (filtered by active/closed) */}
        {(view === 'active' || view === 'closed') && (() => {
          const filtered = view === 'active'
            ? sessions.filter(s => !s.closed)
            : sessions.filter(s => s.closed);
          const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
          const page = Math.min(currentPage, totalPages);
          const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

          return (
            <div className="panel-section">
              <h2>{view === 'active' ? 'Active' : 'Closed'} Sessions ({filtered.length})</h2>
              {filtered.length === 0 ? (
                <p className="no-data">No {view} sessions</p>
              ) : (
                <>
                  <table className={`sessions-table ${view === 'closed' ? 'closed' : ''}`}>
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Created</th>
                        <th>Creator IP</th>
                        <th>Votes</th>
                        <th>Avg</th>
                        <th>Mode</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map(session => (
                        <tr key={session.code}>
                          <td className="code-cell">{session.code}</td>
                          <td>{formatTime(session.created_at)}</td>
                          <td className="ip-cell">
                            <span className="ip-text">{session.creator_ip}</span>
                            <button
                              className="ip-lookup-btn"
                              onClick={() => handleIpLookup(session.creator_ip)}
                              disabled={ipLoading}
                              title="Lookup IP location"
                            >
                              🌍
                            </button>
                            <button
                              className={`ip-lookup-btn ${isIpBanned(session.creator_ip) ? 'banned' : ''}`}
                              onClick={() => isIpBanned(session.creator_ip) ? handleUnbanIp(session.creator_ip) : handleBanIp(session.creator_ip)}
                              title={isIpBanned(session.creator_ip) ? 'Unban IP' : 'Ban IP'}
                            >
                              {isIpBanned(session.creator_ip) ? '✅' : '🚫'}
                            </button>
                          </td>
                          <td>{session.vote_count} / {session.max_voters}</td>
                          <td>{session.average > 0 ? session.average.toFixed(2) : '—'}</td>
                          <td>{session.anonymous ? '🔒 Anon' : 'Named'}</td>
                          <td className="actions-cell">
                            <button
                              className="table-btn view-btn"
                              onClick={() => navigate(`/admin/${session.code}`)}
                            >
                              View
                            </button>
                            {view === 'active' && (
                              <button
                                className="table-btn close-btn"
                                onClick={() => handleCloseSession(session.code)}
                              >
                                Close
                              </button>
                            )}
                            {view === 'closed' && (
                              <button
                                className="table-btn reopen-btn"
                                onClick={() => handleReopenSession(session.code)}
                              >
                                Reopen
                              </button>
                            )}
                            <button
                              className="table-btn delete-btn"
                              onClick={() => handleDeleteSession(session.code)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        className="page-btn"
                        disabled={page <= 1}
                        onClick={() => setCurrentPage(page - 1)}
                      >
                        ← Previous
                      </button>
                      <span className="page-info">Page {page} of {totalPages}</span>
                      <button
                        className="page-btn"
                        disabled={page >= totalPages}
                        onClick={() => setCurrentPage(page + 1)}
                      >
                        Next →
                      </button>
                      <select
                        className="page-size-select"
                        value={pageSize}
                        onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                      >
                        <option value={5}>5 per page</option>
                        <option value={10}>10 per page</option>
                        <option value={25}>25 per page</option>
                      </select>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })()}
      </div>

      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.confirmText}
          variant={confirmModal.variant}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      <ToastContainer
        toasts={toasts}
        onUndo={handleToastUndo}
        onExpire={handleToastExpire}
      />
    </div>
  );
}

export default AdminPanel;
